/**
 * Helpers for converting between local `<input type="datetime-local">` values
 * (`YYYY-MM-DDTHH:mm` in local wall time) and epoch milliseconds / Date /
 * Firestore Timestamp values.
 */

export type LocalDateTimeInputSource =
  | Date
  | number
  | string
  | { toDate(): Date }
  | null
  | undefined

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * Parses a local `YYYY-MM-DDTHH:mm` (or ISO string) into epoch milliseconds,
 * or returns `null` when invalid.
 */
export function fromLocalDateTimeInput(s: unknown): number | null {
  if (typeof s === 'number') {
    return Number.isFinite(s) && !Number.isNaN(new Date(s).getTime()) ? s : null
  }
  if (typeof s !== 'string') return null
  const trimmed = s.trim()
  if (!trimmed) return null

  const match = LOCAL_DATETIME_RE.exec(trimmed)
  if (match) {
    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const hour = Number(match[4])
    const minute = Number(match[5])
    const second = match[6] ? Number(match[6]) : 0

    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null
    }

    const d = new Date(year, month - 1, day, hour, minute, second, 0)
    if (
      Number.isNaN(d.getTime()) ||
      d.getFullYear() !== year ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null
    }
    return d.getTime()
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getTime()
}

/**
 * Formats a Date, epoch ms number, ISO/local string, or Firestore Timestamp
 * (`{ toDate(): Date }`) as `YYYY-MM-DDTHH:mm` in local wall time.
 * Returns `''` when the value cannot be parsed into a valid Date.
 */
export function toLocalDateTimeInput(value: LocalDateTimeInputSource | unknown): string {
  if (value == null) return ''

  let d: Date | null = null

  if (value instanceof Date) {
    d = value
  } else if (typeof value === 'number') {
    if (!Number.isFinite(value)) return ''
    d = new Date(value)
  } else if (typeof value === 'string') {
    const ms = fromLocalDateTimeInput(value)
    if (ms === null) return ''
    d = new Date(ms)
  } else if (typeof value === 'object' && 'toDate' in (value as Record<string, unknown>)) {
    const fn = (value as { toDate?: () => Date }).toDate
    if (typeof fn === 'function') {
      const candidate = fn.call(value)
      if (candidate instanceof Date) {
        d = candidate
      }
    }
  }

  if (!d || Number.isNaN(d.getTime())) return ''

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/**
 * Formats a Date or epoch milliseconds as `YYYY-MM-DD` in local wall time.
 */
export function toLocalIsoDate(value: Date | number = new Date()): string {
  const d = typeof value === 'number' ? new Date(value) : value
  if (!d || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

